/**
 * ForgeVisionLoader — cinematic analytical loading sequence.
 * Stages: satellite zoom → parcel outline → roads → utilities → score
 *         → building mass → ROI → timeline.
 * Respects prefers-reduced-motion. Uses Ice Blue (AI/tech accent) only.
 */
import React, { useEffect, useState, useRef } from "react";

const STAGES = [
    { key: "satellite", label: "Locating satellite imagery" },
    { key: "parcel", label: "Tracing parcel boundary" },
    { key: "roads", label: "Mapping roads & access" },
    { key: "utilities", label: "Inspecting utilities" },
    { key: "score", label: "Scoring opportunity" },
    { key: "mass", label: "Modeling building mass" },
    { key: "roi", label: "Projecting ROI" },
    { key: "timeline", label: "Sequencing timeline" },
];

const STAGE_MS = 550;
const NAVY = "#0F2747";
const ICE = "#DCEEFF";

export default function ForgeVisionLoader({ onDone }) {
    const [stage, setStage] = useState(0);
    const reduced = useRef(typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches);

    useEffect(() => {
        if (reduced.current) { setStage(STAGES.length - 1); return; }
        if (stage >= STAGES.length - 1) return;
        const t = setTimeout(() => setStage((s) => Math.min(s + 1, STAGES.length - 1)), STAGE_MS);
        return () => clearTimeout(t);
    }, [stage]);

    // Optional external "done" callback fired when we reach the last stage
    useEffect(() => {
        if (stage === STAGES.length - 1 && onDone) onDone();
    }, [stage, onDone]);

    return (
        <div className="border border-ice/25 bg-obsidian/60 relative overflow-hidden" data-testid="forge-vision-loading" role="status" aria-live="polite">
            <div className="grid md:grid-cols-[1.1fr_1fr]">
                <div className="relative aspect-[4/3] md:aspect-auto md:min-h-[340px] bg-obsidian overflow-hidden">
                    <ParcelSVG stage={stage} />
                </div>
                <div className="p-6 md:p-8">
                    <div className="chip chip-ai" data-testid="fv-loader-chip">Forge Vision · Analyzing</div>
                    <div className="mt-4 space-y-2" data-testid="fv-loader-stages">
                        {STAGES.map((s, i) => {
                            const state = i < stage ? "done" : i === stage ? "active" : "pending";
                            return (
                                <div key={s.key} className="flex items-center gap-3 text-sm" data-testid={`fv-stage-${s.key}`} data-state={state}>
                                    <StageDot state={state} />
                                    <span className={state === "pending" ? "text-white/30" : state === "active" ? "text-white" : "text-white/60"}>{s.label}</span>
                                    {state === "active" && (
                                        <span className="ml-auto text-[10px] text-ice tracking-widest uppercase" style={{ color: ICE }}>Working</span>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    <div className="mt-6 pt-4 border-t border-white/10">
                        <div className="h-[2px] bg-white/5 relative overflow-hidden">
                            <div
                                className="absolute inset-y-0 left-0 transition-[width] duration-500 ease-out"
                                style={{ width: `${((stage + 1) / STAGES.length) * 100}%`, backgroundColor: ICE }}
                            />
                        </div>
                        <div className="mt-2 text-[10px] uppercase tracking-widest text-white/40">Not investment advice · demonstration analysis</div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function StageDot({ state }) {
    if (state === "done") {
        return (
            <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
                <circle cx="7" cy="7" r="6" fill="none" stroke={ICE} strokeWidth="1.2" />
                <path d="M4 7 L6.2 9.2 L10 5" fill="none" stroke={ICE} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        );
    }
    if (state === "active") {
        return (
            <span className="relative inline-flex h-3 w-3" aria-hidden="true">
                <span className="absolute inset-0 rounded-full animate-ping opacity-40" style={{ backgroundColor: ICE }} />
                <span className="relative inline-block h-3 w-3 rounded-full" style={{ backgroundColor: ICE }} />
            </span>
        );
    }
    return <span className="inline-block h-3 w-3 rounded-full border border-white/20" aria-hidden="true" />;
}

/**
 * ParcelSVG — staged reveal: satellite grid → parcel → roads → utilities
 * → score badge → building mass → ROI line → timeline.
 */
function ParcelSVG({ stage }) {
    const show = (idx) => stage >= idx;
    return (
        <svg viewBox="0 0 400 300" className="w-full h-full" aria-hidden="true">
            {/* Stage 0: satellite grid */}
            <defs>
                <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
                    <path d="M20 0 L0 0 0 20" fill="none" stroke="#DCEEFF" strokeOpacity="0.05" strokeWidth="0.5" />
                </pattern>
                <linearGradient id="scan" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0" stopColor="#DCEEFF" stopOpacity="0" />
                    <stop offset="0.5" stopColor="#DCEEFF" stopOpacity="0.28" />
                    <stop offset="1" stopColor="#DCEEFF" stopOpacity="0" />
                </linearGradient>
            </defs>
            <rect width="400" height="300" fill={NAVY} opacity={show(0) ? 0.4 : 0} style={{ transition: "opacity 400ms" }} />
            <rect width="400" height="300" fill="url(#grid)" opacity={show(0) ? 1 : 0} style={{ transition: "opacity 400ms" }} />

            {/* Scanning bar during satellite stage */}
            {stage === 0 && (
                <rect x="0" y="0" width="400" height="60" fill="url(#scan)">
                    <animateTransform attributeName="transform" type="translate" from="0 -60" to="0 300" dur="1.4s" repeatCount="indefinite" />
                </rect>
            )}

            {/* Stage 1: parcel outline */}
            <polygon
                points="80,60 320,50 340,220 220,260 70,230"
                fill={ICE}
                fillOpacity={show(1) ? 0.06 : 0}
                stroke={ICE}
                strokeWidth="1.4"
                strokeDasharray="720"
                strokeDashoffset={show(1) ? 0 : 720}
                style={{ transition: "stroke-dashoffset 700ms ease-out, fill-opacity 500ms" }}
            />

            {/* Stage 2: roads */}
            <g opacity={show(2) ? 1 : 0} style={{ transition: "opacity 500ms" }}>
                <line x1="0" y1="270" x2="400" y2="235" stroke="#F7F7F4" strokeOpacity="0.35" strokeWidth="6" strokeLinecap="round" />
                <line x1="0" y1="270" x2="400" y2="235" stroke={ICE} strokeOpacity="0.55" strokeWidth="1" strokeDasharray="6 6" />
                <line x1="325" y1="0" x2="345" y2="300" stroke="#F7F7F4" strokeOpacity="0.22" strokeWidth="4" />
            </g>

            {/* Stage 3: utilities */}
            <g opacity={show(3) ? 1 : 0} style={{ transition: "opacity 500ms" }}>
                {[[100, 90], [180, 80], [260, 90], [200, 180], [280, 200]].map(([x, y], i) => (
                    <g key={i}>
                        <circle cx={x} cy={y} r="3" fill={ICE} />
                        <circle cx={x} cy={y} r="8" fill="none" stroke={ICE} strokeOpacity="0.4" />
                    </g>
                ))}
            </g>

            {/* Stage 4: opportunity score badge */}
            <g opacity={show(4) ? 1 : 0} transform="translate(300,80)" style={{ transition: "opacity 500ms" }}>
                <circle r="26" fill={NAVY} stroke={ICE} strokeWidth="1.2" />
                <text textAnchor="middle" dy="2" fontFamily="Cormorant Garamond, serif" fontSize="18" fill={ICE}>82</text>
                <text textAnchor="middle" dy="14" fontSize="6" letterSpacing="1.5" fill="#DCEEFF" opacity="0.7">SCORE</text>
            </g>

            {/* Stage 5: building mass extruded */}
            <g opacity={show(5) ? 1 : 0} style={{ transition: "opacity 600ms" }}>
                <polygon points="170,150 250,140 260,180 180,190" fill={NAVY} stroke={ICE} strokeWidth="1" />
                <polygon points="170,150 180,120 260,110 250,140" fill="#1a2f4d" stroke={ICE} strokeWidth="1" />
                <polygon points="250,140 260,110 270,150 260,180" fill="#0b1c37" stroke={ICE} strokeWidth="1" />
            </g>

            {/* Stage 6: ROI curve */}
            <g opacity={show(6) ? 1 : 0} transform="translate(30,240)" style={{ transition: "opacity 500ms" }}>
                <polyline
                    points="0,20 30,15 60,12 90,6 120,-2 150,-10"
                    fill="none"
                    stroke={ICE}
                    strokeWidth="1.4"
                />
                <text x="0" y="-14" fontSize="7" letterSpacing="2" fill="#DCEEFF" opacity="0.7">ROI · 5YR</text>
            </g>

            {/* Stage 7: timeline ticks */}
            <g opacity={show(7) ? 1 : 0} transform="translate(30,282)" style={{ transition: "opacity 500ms" }}>
                <line x1="0" y1="0" x2="330" y2="0" stroke="#F7F7F4" strokeOpacity="0.3" />
                {[0, 66, 132, 198, 264, 330].map((x, i) => (
                    <g key={i}>
                        <line x1={x} y1="-4" x2={x} y2="4" stroke={ICE} strokeWidth={i === 0 || i === 5 ? "1.5" : "0.8"} />
                        <text x={x} y="14" fontSize="6" letterSpacing="1" fill="#DCEEFF" opacity="0.7" textAnchor="middle">M{i * 6}</text>
                    </g>
                ))}
            </g>
        </svg>
    );
}
